import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetAllCourses } from "./useGetAllCourses";

/**
 * The single source of truth for "how much of the catalogue is free". Every
 * surface aimed at a visitor who has not paid — the landing page, the register
 * panel, the FAQ, the upsell banner — reads the number from here instead of
 * hardcoding it, so changing which courses are free changes the copy too.
 */
export function useFreeCourses() {
	const { t } = useTranslation();
	const { courses = [], isPending } = useGetAllCourses();

	return useMemo(() => {
		const freeCourses = courses.filter((course) => course.is_free);
		const freeCount = freeCourses.length;

		// While the catalogue is still in flight the count is 0, and asserting
		// "0 besplatnih kurseva" on a conversion surface is worse than saying
		// nothing — drop the number until we actually have one. The phrase is
		// in the accusative so it drops into "Otključajte …", "imate …" and
		// "Registracija otključava …" alike, and the English one is written to
		// fit the same English sentences ("Unlock …", "you have … available").
		// It is a finished string: every surface drops it in whole.
		const freeOffer =
			freeCount > 0
				? t("landing:freeOffer.count", { count: freeCount })
				: t("landing:freeOffer.none");

		return {
			courses,
			freeCourses,
			freeCount,
			freeOffer,
			lockedCount: Math.max(courses.length - freeCount, 0),
			isPending,
		};
	}, [courses, isPending, t]);
}
